import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Breadcrumb,
  Collapse,
  Divider,
  Card,
  Space,
  Popconfirm,
  Badge,
} from "antd";
import { useDispatch } from "react-redux";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import userService from "../../services/user-service";
import constantService from "../../services/constant-service";
import CustomTable from "../../components/table/custom-table";
import { useCreatedSort, withCreatedColumn } from '../../shared/functional/created-sort';
import * as Icon from "react-feather";
import { WEBSITE_USER_ROLE, USER_STATUS } from "../../constants/app-constants";
import AddEditWebsiteUserDialog from "./AddEditWebsiteUserDialog";

const { Panel } = Collapse;

// Website Users (Phase 3): front-end users with role WEBSITE_USER.
// Most are provisioned via Google staff login (domain-restricted), but an admin
// can also create one manually and enable/disable an existing account.
const WebsiteUserManagement = () => {
  const dispatch = useDispatch();
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [condition, setCondition] = useState({
    firstName: null,
    lastName: null,
    email: null,
    userRole: WEBSITE_USER_ROLE,
  });
  const [dataList, setDataList] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    rowPerPage: 10,
    rowCount: 0,
  });
  const [statusList, setStatusList] = useState([]);

  useEffect(() => {
    async function initData() {
      const { userStatus } = await constantService.getUserStatus();
      setStatusList(userStatus);
    }
    initData();
    doSearch(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeCondition = (event) => {
    const { name, value } = event.target;
    setCondition({ ...condition, [name]: value });
  };

  /**
   * NMU-529: clearing the filters has to re-run the search, not just empty the
   * inputs — otherwise the table keeps showing the previous result set while the
   * form claims no filter is applied. Back to page 1, since the record the user
   * was looking at on page 3 of a filtered list is somewhere else entirely in
   * the full one.
   */
  const onClearSearch = () => {
    const cleared = {
      firstName: null,
      lastName: null,
      email: null,
      userRole: WEBSITE_USER_ROLE,
    };
    setCondition(cleared);
    doSearch(1, { condition: cleared });
  };

  /**
   * @param currentPage page to land on, 1 to start over.
   * @param overrides   NMU-529: search with these filters instead of the ones in
   *   state. setCondition only takes effect on the next render, so a caller that
   *   resets the filters and searches in the same handler would otherwise re-run
   *   the search it just cleared.
   */
  const doSearch = async (currentPage, overrides) => {
    try {
      const activeSort = overrides && overrides.sort ? overrides.sort : sort;
      const newPaginate = {
        ...pagination,
        currentPage: currentPage ? currentPage : pagination.currentPage,
        // NMU-580: the order travels with the page — see created-sort.js.
        ...activeSort,
      };

      dispatch(appLoaderAction.enable());
      // Always constrain to WEBSITE_USER role.
      const activeCondition = overrides ? overrides.condition : condition;

      const result = await userService.search(
        { ...activeCondition, userRole: WEBSITE_USER_ROLE },
        newPaginate
      );
      setDataList(result.list);
      setPagination(result.pagination ? result.pagination : newPaginate);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      dispatch(notifyUtitls.error(err));
    }
  };

  const onChangePage = (currentPage) => {
    doSearch(currentPage);
  };

  const onAdd = () => {
    setEditData(null);
    setIsShowDialog(true);
  };

  const onCancel = () => {
    setIsShowDialog(false);
    setEditData(null);
  };

  const onSaveComplete = () => {
    setIsShowDialog(false);
    setEditData(null);
    doSearch(1);
  };

  const onClickEdit = async (id) => {
    try {
      dispatch(appLoaderAction.enable());
      const result = await userService.getById({ id: id });
      setEditData(result.bean);
      setIsShowDialog(true);
      dispatch(appLoaderAction.disale());
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  // Enable/disable = update the user's `status` (A active / I inactive) through the
  // existing user edit endpoint. The full bean is re-fetched first so nothing is lost.
  const onToggleStatus = async (id, currentStatus) => {
    try {
      dispatch(appLoaderAction.enable());
      const result = await userService.getById({ id: id });
      const bean = JSON.parse(JSON.stringify(result.bean));
      bean.status =
        currentStatus === USER_STATUS.ACTIVE
          ? USER_STATUS.INACTIVE
          : USER_STATUS.ACTIVE;
      await userService.edit(bean);
      dispatch(appLoaderAction.disale());
      notifyUtitls.success(
        "สำเร็จ",
        currentStatus === USER_STATUS.ACTIVE
          ? "ปิดใช้งานผู้ใช้งานสำเร็จ"
          : "เปิดใช้งานผู้ใช้งานสำเร็จ"
      );
      doSearch(pagination.currentPage);
    } catch (err) {
      dispatch(appLoaderAction.disale());
      notifyUtitls.error(err);
    }
  };

  /*
    NMU-580: the วันที่/เวลาสร้าง column and the sort behind it.

    The server does the ordering — this table shows one page of many — so a
    click re-runs the search in the new order, back on page 1: the newest
    record is not on page 3 of the same list read the other way round.
  */
  const { sort, createdColumn, onChangeTable } = useCreatedSort((next) =>
    doSearch(1, { condition, sort: next }));

  const columns = [
    {
      align: "left",
      title: "ชื่อ",
      key: "firstName",
      render: ({ profile }) => <span>{profile?.firstName}</span>,
    },
    {
      align: "left",
      title: "นามสกุล",
      key: "lastName",
      render: ({ profile }) => <span>{profile?.lastName}</span>,
    },
    {
      align: "left",
      title: "อีเมล",
      key: "email",
      render: ({ profile }) => <span>{profile?.email}</span>,
    },
    {
      align: "left",
      title: "เบอร์โทรศัพท์",
      key: "telNo",
      render: ({ profile }) => <span>{profile?.telNo}</span>,
    },
    {
      align: "left",
      title: "สถานะ",
      key: "status",
      dataIndex: "status",
      render: (data) => {
        const status = statusList.find((item) => item.value === data);
        const label = status ? status.labelEn : data;
        return data === USER_STATUS.ACTIVE ? (
          <Badge color="#4A9A0F" className="text-nowrap" text={label} />
        ) : (
          <Badge color="#C0C0C0" className="text-nowrap" text={label} />
        );
      },
    },
    {
      title: "ดำเนินการ",
      key: "action",
      fixed: "right",
      width: 140,
      render: ({ userId, status }) => (
        <Space size="middle">
          <label
            style={{ cursor: "pointer" }}
            onClick={() => onClickEdit(userId)}
            className="pe-3 border-end"
          >
            <Icon.Edit size={18} />
          </label>

          <Popconfirm
            title={
              status === USER_STATUS.ACTIVE
                ? "ยืนยันการปิดใช้งานผู้ใช้งานนี้ ?"
                : "ยืนยันการเปิดใช้งานผู้ใช้งานนี้ ?"
            }
            onConfirm={() => onToggleStatus(userId, status)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
          >
            <label style={{ cursor: "pointer" }}>
              {status === USER_STATUS.ACTIVE ? (
                <Icon.UserX size={18} />
              ) : (
                <Icon.UserCheck size={18} />
              )}
            </label>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2 className="color-primary-title">ผู้ใช้งานเว็บไซต์</h2>
      <Breadcrumb>
        <Breadcrumb.Item>ผู้ดูแลระบบ</Breadcrumb.Item>
        <Breadcrumb.Item>ผู้ใช้งานเว็บไซต์</Breadcrumb.Item>
      </Breadcrumb>
      <Divider className="mt-2 mb-4" />

      <div className="d-flex justify-content-between">
        <div>
          <Button
            type="primary"
            className="mx-2"
            htmlType="button"
            onClick={() => onAdd()}
          >
            <Icon.Plus className="me-1" size={18} /> เพิ่มผู้ใช้งานเว็บไซต์
          </Button>
        </div>
      </div>

      <Card className="mt-4" bordered={false}>
        <Collapse className="mb-4" defaultActiveKey={["1"]}>
          <Panel header="ตัวกรอง" key="1" className="search-panel">
            <Form wrapperCol={{ span: 22 }} layout="vertical">
              <Row>
                <Col xs={24} sm={8} xxl={{ span: 4, offset: 4 }}>
                  <Form.Item label="ชื่อ" className="mb-0">
                    <Input
                      name="firstName"
                      value={condition.firstName}
                      onChange={onChangeCondition}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} xxl={{ span: 4, offset: 4 }}>
                  <Form.Item label="นามสกุล">
                    <Input
                      name="lastName"
                      value={condition.lastName}
                      onChange={onChangeCondition}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={8} xxl={{ span: 4, offset: 4 }}>
                  <Form.Item label="อีเมล">
                    <Input
                      name="email"
                      value={condition.email}
                      onChange={onChangeCondition}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div>
                <Button
                  type="primary"
                  className="mx-2"
                  onClick={() => doSearch(1)}
                >
                  <Icon.Search className="me-1" size={18} />
                  ค้นหา
                </Button>
                <Button className="btn-gray ms-2" onClick={() => onClearSearch()}>
                  ล้างค่า
                </Button>
              </div>
            </Form>
          </Panel>
        </Collapse>

        <CustomTable
          rowKey="userId"
          tableName="websiteUserTable"
          columns={withCreatedColumn(columns, createdColumn)}
          onChangeTable={onChangeTable}
          data={dataList}
          scroll={{ x: true }}
          isPagination={true}
          onChangePage={onChangePage}
          currentPage={pagination.currentPage}
          pageSize={pagination.rowPerPage}
          totalRecord={pagination.rowCount}
        />
      </Card>

      <AddEditWebsiteUserDialog
        isShow={isShowDialog}
        editData={editData}
        onCancel={onCancel}
        onSaveComplete={onSaveComplete}
      />
    </div>
  );
};

export default WebsiteUserManagement;
